import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUp, CalendarHeart, Camera, Compass, FileText, ImageIcon, Loader2, LocateFixed, MapPin, Paperclip, Plane, Sparkles, Stamp, X } from 'lucide-react';
import { Photo } from '../../lib/imagery';
import { Link } from 'react-router';
import { useApp, useCapability } from '../../data/AppProvider';
import { errorMessage, keys, useMe } from '../../data/hooks';
import { DataError } from '../../data/source';
import type { ChatAction, ChatAttachment, ChatMessage } from '../../domain/assistant';
import { deviceTimeZone } from '../../domain/time';
import { toAttachment } from '../../lib/attachments';
import { cx } from '../ui';
import { ActionCard } from './ActionCard';
import { Blocks } from './Blocks';
import type { Block } from '../../domain/blocks';
import { RichText } from './RichText';

type Loc = { lat: number; lon: number; accuracy_m: number | null };

export function ElioChat({ conversationId, tripId, tripTitle, onConversationCreated, onTripChange, suggestions, header, compact, initialText, initialFiles, variant = 'panel' }: {
  conversationId: string | null;
  tripId: string | null;
  tripTitle?: string | null;
  onConversationCreated: (id: string) => void;
  onTripChange?: (tripId: string) => void;
  suggestions: string[];
  header?: ReactNode;
  compact?: boolean;
  /** Text handed over from elsewhere in the app (e.g. the Now screen); sent once Elio is ready. */
  initialText?: string | null;
  /** Files handed over (e.g. from the travel wallet); sent with initialText once Elio is ready. */
  initialFiles?: ChatAttachment[] | null;
  /** 'home' shows the full landing page when the conversation is empty. */
  variant?: 'home' | 'panel';
}) {
  const { data, session, announce } = useApp();
  const qc = useQueryClient();
  const me = useMe();
  const capability = useCapability('assistant');
  const conv = useQuery({ queryKey: keys.conversation(conversationId ?? 'none'), queryFn: () => data.getConversation(conversationId!), enabled: Boolean(conversationId && session) });
  const [text, setText] = useState('');
  const [files, setFiles] = useState<ChatAttachment[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [location, setLocation] = useState<Loc | null>(null);
  const [locating, setLocating] = useState(false);
  const [pending, setPending] = useState<{ text: string; attachments: { name: string; kind: 'image' | 'pdf' }[] } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<{ message: string; setup?: string } | null>(null);
  const [consenting, setConsenting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const messages: ChatMessage[] = conv.data?.messages ?? [];
  const actions = useMemo(() => new Map((conv.data?.actions ?? []).map((a) => [a.id, a])), [conv.data?.actions]);
  const consent = me.data?.settings.ai_processing_consent ?? false;
  const isDemo = data.kind === 'demo';
  const configured = capability?.configured ?? false;
  const blocked = isDemo || !configured;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages.length, pending, conv.data?.actions]);

  const addFiles = async (list: FileList | null) => {
    if (!list?.length) return;
    setPreparing(true); setError(null);
    try {
      const next = [...files];
      for (const f of Array.from(list).slice(0, 4 - files.length)) next.push(await toAttachment(f));
      setFiles(next);
      inputRef.current?.focus();
    } catch (e) { setError({ message: e instanceof Error ? e.message : 'Could not read that file.' }); }
    finally { setPreparing(false); if (fileRef.current) fileRef.current.value = ''; if (cameraRef.current) cameraRef.current.value = ''; if (photoRef.current) photoRef.current.value = ''; }
  };

  const shareLocation = () => {
    if (location) { setLocation(null); return; }
    if (!navigator.geolocation) { setError({ message: 'This browser cannot share location.' }); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setLocation({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy_m: p.coords.accuracy ?? null }); setLocating(false); announce('Location will be shared with your next message.'); },
      () => { setLocating(false); setError({ message: 'Location wasn’t shared. You can allow it in your browser settings, or just tell Elio where you are.' }); },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 },
    );
  };

  const send = async (override?: string, overrideFiles?: ChatAttachment[]) => {
    const body = (override ?? text).trim();
    const atts = overrideFiles ?? files;
    if ((!body && atts.length === 0) || pending) return;
    setError(null);
    setPending({ text: body, attachments: atts.map((a) => ({ name: a.name, kind: a.kind })) });
    setText(''); setFiles([]);
    try {
      const res = await data.sendChat({
        conversationId, tripId, text: body, attachments: atts,
        client: { timeZone: deviceTimeZone() ?? null, location },
      }, setStatus);
      setLocation(null);
      qc.setQueryData(keys.conversation(res.conversationId), (old: { conversation: unknown; messages: ChatMessage[]; actions: ChatAction[] } | undefined) => ({
        conversation: old?.conversation ?? { id: res.conversationId, trip_id: res.tripId, title: '', last_message_at: new Date().toISOString(), created_at: new Date().toISOString() },
        messages: [...(old?.messages ?? []), res.userMessage, res.assistantMessage],
        actions: [...(old?.actions ?? []), ...res.actions],
      }));
      void qc.invalidateQueries({ queryKey: keys.conversations });
      void qc.invalidateQueries({ queryKey: keys.memory });
      if (!conversationId) onConversationCreated(res.conversationId);
    } catch (e) {
      setText(body); setFiles(atts);
      const setup = e instanceof DataError && e.detail?.startsWith('setup:') ? e.detail.slice(6) : undefined;
      setError({ message: errorMessage(e), setup });
    } finally { setPending(null); setStatus(null); }
  };

  const submit = (e: FormEvent) => { e.preventDefault(); void send(); };

  const turnOn = async () => {
    setConsenting(true);
    try { await data.updateSettings({ ai_processing_consent: true }); await qc.invalidateQueries({ queryKey: keys.me }); setError(null); }
    catch (e) { setError({ message: errorMessage(e) }); }
    finally { setConsenting(false); }
  };

  const handedOver = useRef(false);
  useEffect(() => {
    if (handedOver.current || (!initialText && !initialFiles?.length) || !me.data) return;
    handedOver.current = true;
    if (!blocked && consent) void send(initialText ?? '', initialFiles ?? undefined);
    else { setText(initialText ?? ''); if (initialFiles?.length) setFiles(initialFiles); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialText, me.data, blocked, consent]);

  const loadingConv = Boolean(conv.isLoading && conversationId);
  const empty = messages.length === 0 && !pending && !loadingConv;
  const disabled = blocked || !consent;
  const pick = (s: string) => (disabled ? setText(s) : void send(s));

  const gate = (
    <>
      {isDemo ? (
        <Banner icon={<Sparkles className="size-4" />}>Elio isn’t available in the demo, which uses fictional data. Sign in to talk to Elio about your own trips.</Banner>
      ) : !configured ? (
        <Banner icon={<Sparkles className="size-4" />}>Elio is being set up for this environment. Your trips, bookings and documents still work as usual.</Banner>
      ) : !consent || error?.setup === 'ai_consent' ? (
        <div className="mb-2 flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-white px-4 py-2.5 text-left text-sm">
          <span className="min-w-0 flex-[1_1_10rem] text-muted">Elio is turned off for your account.</span>
          <button type="button" onClick={turnOn} disabled={consenting} className="inline-flex min-h-9 items-center gap-2 rounded-full bg-forest-700 px-4 font-semibold text-white hover:bg-forest-800 disabled:opacity-60">
            {consenting && <Loader2 className="size-4 animate-spin" aria-hidden />}Turn on
          </button>
        </div>
      ) : null}
      {error && error.setup !== 'ai_consent' && <p className="mb-2 rounded-xl bg-danger-soft px-3 py-2 text-left text-sm text-danger" role="alert">{error.message}</p>}
    </>
  );

  const composer = (big: boolean) => (
    <form onSubmit={submit} className={cx('rounded-[28px] border bg-white p-2 text-left transition focus-within:border-turquoise-500/60 focus-within:shadow-[0_0_0_4px_rgb(41_199_194/0.15),var(--shadow-lift)]', big ? 'border-line shadow-[var(--shadow-lift)]' : 'border-line shadow-[var(--shadow-lift)]', disabled && 'opacity-60')}>
      {(files.length > 0 || location) && (
        <ul className="flex flex-wrap gap-2 px-2 pt-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-2 rounded-xl border border-line bg-ivory py-1 pl-1 pr-1 text-sm">
              {f.kind === 'image' ? <img src={f.dataUrl} alt="" className="size-9 rounded-lg object-cover" /> : <span className="flex size-9 items-center justify-center rounded-lg bg-coral-50 text-coral-700"><FileText className="size-4" aria-hidden /></span>}
              <span className="max-w-40 truncate">{f.name}</span>
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="flex size-8 items-center justify-center rounded-lg hover:bg-white"><X className="size-4" /></button>
            </li>
          ))}
          {location && <li className="flex items-center gap-1.5 rounded-xl bg-ocean-50 px-3 py-1 text-sm text-ocean-700"><MapPin className="size-4" aria-hidden />Location attached</li>}
        </ul>
      )}
      <label htmlFor={inputId} className="sr-only">Message Elio</label>
      <textarea id={inputId} ref={inputRef} rows={big ? 2 : compact ? 1 : 2} value={text} maxLength={4000} disabled={disabled} autoFocus={big}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); } }}
        placeholder={tripTitle && !big ? `Ask about ${tripTitle}, or share a ticket or photo…` : 'Ask Elio anything about travel…'}
        className={cx('block max-h-48 w-full resize-none border-0 bg-transparent px-3 py-2 text-ink placeholder:text-muted/80 focus:outline-none', big ? 'min-h-16 text-lg' : 'min-h-11 text-base')} />
      <div className="flex items-center gap-0.5 px-1">
        <ToolButton label="Attach a document (PDF or image)" onClick={() => fileRef.current?.click()} disabled={disabled || files.length >= 4}><Paperclip className="size-5" /></ToolButton>
        <ToolButton label="Upload photos" onClick={() => photoRef.current?.click()} disabled={disabled || files.length >= 4}><ImageIcon className="size-5" /></ToolButton>
        <ToolButton label="Take a photo" onClick={() => cameraRef.current?.click()} disabled={disabled || files.length >= 4}><Camera className="size-5" /></ToolButton>
        <ToolButton label={location ? 'Stop sharing location' : 'Share my location with this message'} onClick={shareLocation} disabled={disabled} active={Boolean(location)}>
          {locating ? <Loader2 className="size-5 animate-spin" /> : <LocateFixed className="size-5" />}
        </ToolButton>
        {preparing && <span className="ml-1 text-xs text-muted">Preparing…</span>}
        <button type="submit" aria-label="Send" disabled={disabled || Boolean(pending) || preparing || (!text.trim() && files.length === 0)}
          className="ml-auto inline-flex size-11 items-center justify-center rounded-full bg-gradient-to-br from-forest-700 to-turquoise-700 text-white shadow-sm transition hover:brightness-110 disabled:from-forest-200 disabled:to-forest-200">
          {pending ? <Loader2 className="size-5 animate-spin" /> : <ArrowUp className="size-5" />}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => void addFiles(e.target.files)} />
      <input ref={photoRef} type="file" accept="image/*" multiple className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => void addFiles(e.target.files)} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only" tabIndex={-1} aria-hidden onChange={(e) => void addFiles(e.target.files)} />
    </form>
  );

  if (empty && variant === 'home') {
    return <Landing onPick={pick}>{gate}{composer(true)}</Landing>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {header}
      <div className="min-h-0 flex-1 overflow-y-auto px-1 pb-4" aria-live="polite">
        {loadingConv ? (
          <p className="flex items-center gap-2 p-6 text-sm text-muted"><Loader2 className="size-4 animate-spin" aria-hidden />Loading conversation…</p>
        ) : empty ? (
          <Welcome tripTitle={tripTitle ?? null} suggestions={suggestions} onPick={pick} />
        ) : (
          <ol className={cx('mx-auto space-y-5 py-4', variant === 'home' && 'max-w-3xl')}>
            {messages.map((m) => (
              <li key={m.id}>
                {m.role === 'user' ? <UserBubble text={m.text} attachments={m.attachments} /> : (
                  <ElioBubble text={m.text} citations={m.citations} blocks={m.blocks} onAsk={pending ? undefined : pick}>
                    {m.action_ids.map((id) => actions.get(id)).filter(Boolean).map((a) => (
                      <ActionCard key={a!.id} action={a!} conversationId={conv.data?.conversation ? (conv.data.conversation as { id: string }).id : conversationId!} conversationTripId={tripId}
                        onTripCreated={(t) => onTripChange?.(t)} />
                    ))}
                  </ElioBubble>
                )}
              </li>
            ))}
            {pending && (
              <>
                <li><UserBubble text={pending.text} attachments={pending.attachments} /></li>
                <li className="flex items-center gap-3 pl-1 text-sm text-muted">
                  <ElioAvatar />
                  <span className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 shadow-[var(--shadow-card)]">
                    <span className="flex gap-1" aria-hidden><Dot d={0} /><Dot d={150} /><Dot d={300} /></span>
                    {status ?? (pending.attachments.length ? 'Reading your file…' : 'Thinking…')}
                  </span>
                </li>
              </>
            )}
          </ol>
        )}
        <div ref={endRef} />
      </div>

      <div className={cx('sticky bottom-0 bg-gradient-to-t from-ivory via-ivory to-ivory/0 pt-3', variant === 'home' && 'mx-auto w-full max-w-3xl')}>
        {gate}
        {composer(false)}
        <p className="px-3 pb-1 pt-1.5 text-center text-[0.6875rem] text-muted">Elio can make mistakes. Check times, platforms and entry rules with the operator or authority. <Link to="/privacy#elio" className="underline underline-offset-2 hover:text-ink">How Elio uses your data</Link></p>
      </div>
    </div>
  );
}

function Dot({ d }: { d: number }) {
  return <span className="size-1.5 animate-bounce rounded-full bg-forest-500" style={{ animationDelay: `${d}ms` }} />;
}

function ToolButton({ label, onClick, children, disabled, active }: { label: string; onClick: () => void; children: ReactNode; disabled?: boolean; active?: boolean }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled} aria-pressed={active}
      className={cx('inline-flex size-11 items-center justify-center rounded-full transition disabled:opacity-40', active ? 'bg-ocean-50 text-ocean-700' : 'text-muted hover:bg-ivory hover:text-ink')}>
      {children}
    </button>
  );
}

function Banner({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return <p className="mb-2 flex items-start gap-2 rounded-2xl border border-line bg-white px-4 py-3 text-sm text-muted"><span className="mt-0.5 text-ocean-600">{icon}</span>{children}</p>;
}

export function ElioAvatar({ size = 'md' }: { size?: 'md' | 'lg' }) {
  return <img src="/icon.svg" alt="" className={cx('shrink-0 rounded-full shadow-sm', size === 'lg' ? 'size-14' : 'size-8')} />;
}

function UserBubble({ text, attachments }: { text: string; attachments: { name: string; kind: string }[] }) {
  return (
    <div className="flex flex-col items-end gap-1.5 pl-10">
      {attachments.length > 0 && (
        <ul className="flex flex-wrap justify-end gap-1.5">
          {attachments.map((a, i) => <li key={i} className="flex items-center gap-1.5 rounded-xl border border-line bg-white px-2.5 py-1 text-xs text-muted">{a.kind === 'pdf' ? <FileText className="size-3.5" aria-hidden /> : <ImageIcon className="size-3.5" aria-hidden />}{a.name}</li>)}
        </ul>
      )}
      {text && <p className="max-w-[85%] whitespace-pre-wrap rounded-3xl rounded-br-lg bg-ink px-4 py-2.5 text-[0.9375rem] text-white break-anywhere">{text}</p>}
    </div>
  );
}

function ElioBubble({ text, citations, blocks, onAsk, children }: { text: string; citations: { url: string; title: string }[]; blocks?: Block[]; onAsk?: (t: string) => void; children?: ReactNode }) {
  return (
    <div className="flex gap-3 pr-2 sm:pr-10">
      <ElioAvatar />
      <div className="min-w-0 flex-1">
        <div className="rounded-3xl rounded-tl-lg bg-white px-4 py-3 text-[0.9375rem] leading-relaxed shadow-[var(--shadow-card)]">
          <RichText text={text} />
          {citations.length > 0 && (
            <div className="mt-3 border-t border-line pt-2">
              <p className="text-xs font-semibold text-muted">Sources</p>
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {citations.slice(0, 6).map((c) => (
                  <li key={c.url}><a href={c.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex max-w-56 items-center gap-1 truncate rounded-full bg-ocean-50 px-2.5 py-0.5 text-xs text-ocean-700 hover:underline">{c.title || new URL(c.url).hostname}</a></li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {blocks && blocks.length > 0 && <Blocks blocks={blocks} onAsk={onAsk} />}
        {children}
      </div>
    </div>
  );
}

function Welcome({ tripTitle, suggestions, onPick }: { tripTitle: string | null; suggestions: string[]; onPick: (s: string) => void }) {
  const me = useMe();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const first = (me.data?.profile.display_name ?? '').replace(/\(.*?\)/g, '').trim().split(/\s+/)[0];
  return (
    <div className="fade-in mx-auto flex max-w-xl flex-col items-center px-2 py-10 text-center">
      <ElioAvatar size="lg" />
      <h2 className="mt-4 font-display text-2xl sm:text-3xl">{greeting}{first ? `, ${first}` : ''}!</h2>
      <p className="mt-1 text-muted">{tripTitle ? <>I’m up to speed on <strong className="text-ink">{tripTitle}</strong>. Ask me anything, or share a ticket, booking or photo.</> : 'Where can I take you? Tell me about a trip, ask a question, or share a booking.'}</p>
      <ul className="mt-6 flex w-full flex-col gap-2">
        {suggestions.map((s) => (
          <li key={s}><button type="button" onClick={() => onPick(s)} className="lift w-full rounded-2xl border border-line bg-white px-4 py-3 text-left text-[0.9375rem] hover:border-forest-200">{s}</button></li>
        ))}
      </ul>
    </div>
  );
}

const QUICK: { label: string; prompt: string; icon: ReactNode; tone: string }[] = [
  { label: 'Plan a trip', prompt: 'Help me plan a trip.', icon: <Plane className="size-4" />, tone: 'text-forest-700 bg-forest-50' },
  { label: 'Explore destinations', prompt: 'Inspire me — where could I go next?', icon: <Compass className="size-4" />, tone: 'text-ocean-700 bg-ocean-50' },
  { label: 'My next adventure', prompt: 'What’s coming up on my next trip, and what still needs doing?', icon: <CalendarHeart className="size-4" />, tone: 'text-coral-700 bg-coral-50' },
  { label: 'Travel requirements', prompt: 'What documents and entry requirements should I check for my next trip?', icon: <Stamp className="size-4" />, tone: 'text-gold-ink bg-sun-50' },
];

/** The home page: one question, one input, four starters, and a little travel photography. */
function Landing({ onPick, children }: { onPick: (s: string) => void; children: ReactNode }) {
  const me = useMe();
  const first = (me.data?.profile.display_name ?? '').replace(/\(.*?\)/g, '').trim().split(/\s+/)[0];
  return (
    <div className="fade-in relative mx-auto flex min-h-[calc(100dvh-8rem)] w-full max-w-3xl flex-col items-center justify-center pb-10 pt-4 text-center lg:min-h-[calc(100dvh-3rem)]">
      <div className="relative mb-7 flex h-28 w-full max-w-md items-end justify-center sm:h-36" aria-hidden>
        <Photo slot="lisbon" width={360} className="absolute left-[8%] top-3 h-24 w-24 -rotate-6 rounded-3xl shadow-[var(--shadow-lift)] ring-4 ring-white sm:h-28 sm:w-28" />
        <Photo slot="coast" width={420} className="relative z-10 h-28 w-28 rounded-[2rem] shadow-[var(--shadow-lift)] ring-4 ring-white sm:h-36 sm:w-36" />
        <Photo slot="mountains" width={360} className="absolute right-[8%] top-3 h-24 w-24 rotate-6 rounded-3xl shadow-[var(--shadow-lift)] ring-4 ring-white sm:h-28 sm:w-28" />
        <span className="absolute -bottom-3 left-1/2 z-20 -translate-x-1/2"><img src="/icon.svg" alt="" className="size-11 rounded-full shadow-md ring-4 ring-ivory" /></span>
      </div>
      <h1 className="font-editorial text-4xl leading-tight text-ink sm:text-6xl">Where to next{first ? `, ${first}` : ''}?</h1>
      <p className="mt-2 text-lg text-muted">Your journey starts with a conversation.</p>
      <div className="mt-7 w-full">{children}</div>
      <Link to="/privacy#elio" className="mt-2 text-xs text-muted underline-offset-2 hover:text-ink hover:underline">How Elio uses your data</Link>
      <ul className="mt-5 flex flex-wrap justify-center gap-2">
        {QUICK.map((q) => (
          <li key={q.label}>
            <button type="button" onClick={() => onPick(q.prompt)} className="lift inline-flex min-h-11 items-center gap-2 rounded-full border border-line bg-white py-1.5 pl-1.5 pr-4 text-sm font-medium shadow-[var(--shadow-card)]">
              <span className={cx('flex size-8 items-center justify-center rounded-full', q.tone)}>{q.icon}</span>{q.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
